import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Button from "components/basic/button";
import { headerOffset } from "components/basic/header";
import { DEFAULT_PROFILE_TAB_HEIGHT } from "components/basic/tabs";
import { useUpdateAccountMutation } from "generated/graphql";
import { useSnack } from "hooks/useSnack";
import {
  BUSINESS_NUMBER_PLACEHOLDER,
  formatBusinessNumber,
} from "pages/formTools";
import { ORGANIZATION_TYPES } from "pages/userAuthentication/signUp/constants";
import { DashboardProfileData } from "../..";
import { ProfileEditButton } from "../../components/ProfileEditButton";
import { useVendorFieldSave } from "../../helpers/useVendorFieldSave";
import MailingAddressSection from "./components/MailingAddressSection";

interface BusinessProfileDashboardProps {
  /** Where the profile tabs live: "profile", or "application" before approval. */
  basePath?: string;
  dashboardProfileData: DashboardProfileData;
  onAccountChange: (name: string, value: string | boolean) => void;
  onVendorChange: (name: string, value: string | boolean) => void;
}

const BusinessProfileDashboard: React.FC<BusinessProfileDashboardProps> = ({
  basePath = "profile",
  dashboardProfileData,
  onAccountChange,
  onVendorChange,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const showSnack = useSnack();
  const { t } = useTranslation("profile");
  const { t: tAccount } = useTranslation("account");
  const { vendor, account } = dashboardProfileData;
  const saveVendor = useVendorFieldSave();
  const [, updateAccount] = useUpdateAccountMutation();
  const [mailingIncomplete, setMailingIncomplete] = useState(false);

  // The vendor's address is resolved from the account that registered it.
  const handleAddressSave = async (
    updates: Record<string, string | boolean | number>,
  ) => {
    const response = await updateAccount({ options: updates });
    const error =
      response.data?.updateAccount.errors?.[0].message ??
      response.error?.message;
    showSnack(error ?? t("saved"), error ? "error" : "success");
    return !error;
  };

  const handleMailingSave = async (updates: {
    mailingSameAsBusiness: boolean;
    mailingAddress?: string | null;
  }) => {
    const saved = await saveVendor(updates);
    if (saved) {
      onVendorChange("mailingSameAsBusiness", updates.mailingSameAsBusiness);
      onVendorChange("mailingAddress", updates.mailingAddress ?? "");
    }
    return saved;
  };

  const handleIncompleteChange = useCallback(
    (incomplete: boolean) => setMailingIncomplete(incomplete),
    [],
  );

  const handleNext = () => {
    if (mailingIncomplete) {
      showSnack(t("mailing.incomplete"), "error");
      return;
    }
    navigate(`${basePath}/documents`);
  };

  if (!vendor) {
    return null;
  }

  const buttonStyle: React.CSSProperties = {
    color: theme.palette.common.white,
    fontSize: "1.2em",
    fontWeight: "bold",
  };

  return (
    <div
      style={{
        display: "flex",
        overflow: "auto",
        scrollbarWidth: "thin",
        width: "100%",
        height: `calc(100vh - ${headerOffset(DEFAULT_PROFILE_TAB_HEIGHT + 1)})`,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
          padding: theme.spacing(1.8),
          // Stretches to the parent's fixed viewport height, so the padding
          // has to sit inside it or it forces a scrollbar.
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            maxWidth: "560px",
            overflowY: "auto",
            scrollbarWidth: "thin",
          }}
        >
          <ProfileEditButton
            label={t("fields.business_name")}
            name="businessName"
            input={vendor.businessName}
            onChange={onVendorChange}
            onSave={saveVendor}
          />

          <ProfileEditButton
            label={t("fields.operating_as")}
            name="operatingAs"
            input={vendor.operatingAs ?? ""}
            onChange={onVendorChange}
            onSave={saveVendor}
            description={t("fields.operating_as_hint")}
          />

          <ProfileEditButton
            label={t("fields.organization_type")}
            name="organizationType"
            input={{
              selectedValue: vendor.organizationType,
              optionList: ORGANIZATION_TYPES.map(({ id, label, labelKey }) => ({
                id,
                label: tAccount(labelKey),
                value: label,
              })),
            }}
            onChange={onVendorChange}
            onSave={saveVendor}
            type="dropdown"
          />

          <ProfileEditButton
            label={t("fields.business_address")}
            name="address"
            input={account.address ?? ""}
            onChange={onAccountChange}
            onSave={handleAddressSave}
            type="address"
          />

          <MailingAddressSection
            businessAddress={account.address}
            sameAsBusiness={vendor.mailingSameAsBusiness}
            mailingAddress={vendor.mailingAddress}
            onSave={handleMailingSave}
            onIncompleteChange={handleIncompleteChange}
          />

          <ProfileEditButton
            label={t("fields.business_number")}
            name="businessNumber"
            input={vendor.businessNumber ?? ""}
            onChange={onVendorChange}
            onSave={saveVendor}
            mask={formatBusinessNumber}
            placeholder={BUSINESS_NUMBER_PLACEHOLDER}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            paddingTop: theme.spacing(2),
            gap: theme.spacing(2),
          }}
        >
          <Button
            style={buttonStyle}
            // Two 80px-padded buttons overflow a phone-width row.
            sx={{ paddingX: { xs: 4, sm: 10 }, paddingY: 0 }}
            variant="contained"
            color="primary"
            onClick={() => navigate(`${basePath}/primary_contact`)}
            size="large"
          >
            {t("back")}
          </Button>

          <Button
            style={buttonStyle}
            sx={{ paddingX: { xs: 4, sm: 10 }, paddingY: 0 }}
            variant="contained"
            color="warning"
            onClick={handleNext}
            size="large"
          >
            {t("next")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BusinessProfileDashboard;
